import React from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Grid, Typography } from '@mui/material';
import { Form, Formik, FormikHelpers } from 'formik';
import * as yup from 'yup';
import { usePostHog } from 'posthog-js/react';
import { KeyedMutator } from 'swr/_internal';
import { AccountData, AccountService, PassOrderPageData } from '../../../api';
import { UseAuthPermissions } from '../../../session/UseAuthPermissions';
import { useAuth } from '../../../session/InternalAuthProvider';
import { FormTextField } from '../../../experienceSettings/formComponents/FormTextField';
import { getCurrencySymbol } from '../../../utils/utils';
import { SectionSaveRegistrar } from '../../../experienceSettings/saveHeader/SectionSaveRegistrar';
import type { ChangeEntry } from '../../../experienceSettings/saveHeader/SaveHeaderContext';
import { PassSettingsPageTabs } from '../PassSettingsPageTabs';

type Props = {
  passOrderPage: PassOrderPageData;
  updateLocalAccount: KeyedMutator<AccountData>;
};

type FormValues = {
  price: number;
  weekly_session_count: number | undefined;
};

export const FixedSchedulePricingSection = ({ passOrderPage, updateLocalAccount }: Props) => {
  const { t } = useTranslation();
  const { readOnly } = UseAuthPermissions();
  const { authState } = useAuth();
  const currencySymbol = getCurrencySymbol(authState.account as unknown as AccountData);
  const posthog = usePostHog();

  const validationSchema = yup.object().shape({
    price: yup.number().positive(t('editOrderPage.yup.hasToBePoitive')).required(t('editOrderPage.yup.required')),
    weekly_session_count: yup
      .number()
      .transform((val, orig) => (orig === '' || orig === null ? undefined : val))
      .integer()
      .min(1, t('editOrderPage.yup.hasToBePoitive'))
      .required(t('editOrderPage.yup.required')),
  });

  const initialValues: FormValues = {
    price: passOrderPage.price,
    weekly_session_count: passOrderPage.weekly_session_count,
  };

  const handleSubmit = async (values: FormValues, { setStatus }: FormikHelpers<FormValues>) => {
    try {
      posthog.capture('Editing pass settings', { section: 'pricingSection', passOrderPageId: passOrderPage.id });
      const updatedAccount = await AccountService.updatePassOrderPage({
        id: passOrderPage.id,
        price: values.price,
        weekly_session_count: values.weekly_session_count,
      });
      await updateLocalAccount(updatedAccount);
    } catch (e) {
      setStatus({ error: (e as Error)?.message || t('saveHeader.errorGeneric') });
      throw e;
    }
  };

  const getChanges = (values: FormValues, iv: FormValues): ChangeEntry[] => {
    const changes: ChangeEntry[] = [];
    const compare = (key: keyof FormValues, label: string, suffix = '') => {
      if (values[key] !== iv[key]) {
        changes.push({
          label,
          from: iv[key] != null ? `${iv[key]}${suffix}` : '—',
          to: values[key] != null ? `${values[key]}${suffix}` : '—',
        });
      }
    };
    compare('price', t('passes.price'), ` ${currencySymbol}`);
    compare('weekly_session_count', t('passes.weekly_session_count'));
    return changes;
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}>
      {(formik) => {
        const { isSubmitting } = formik;
        const formIsDisabled = readOnly || isSubmitting;
        return (
          <Form onSubmit={formik.handleSubmit}>
            <SectionSaveRegistrar tabKey={PassSettingsPageTabs.PRICING} getChanges={getChanges} disabled={readOnly} />
            <Grid container justifyContent="space-between" flexWrap="nowrap" alignItems="flex-start">
              <Grid container flexDirection="column" gap={1}>
                <FormTextField
                  type="number"
                  width={200}
                  fieldName="price"
                  label={t('passes.price')}
                  endAdornment={currencySymbol}
                  disabled={formIsDisabled}
                  isFieldUpdate
                />
                <FormTextField
                  type="number"
                  width={200}
                  fieldName="weekly_session_count"
                  label={t('passes.weekly_session_count')}
                  disabled={formIsDisabled}
                  isFieldUpdate
                />
                <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 520 }}>
                  {t('passes.weekly_session_count_helper')}
                </Typography>

                <Divider sx={{ my: 2 }} />
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
